/**
 * Selection in the preview (W2-02; DESIGN.md §4.6 "lane <-> preview linking", §4.8, §5.6.6; ARCHITECTURE.md §7.4):
 *  - outlines of the selected notes (their quads in this frame) and lines;
 *  - click a NOTE to select it (`engine.pickNote` on the frame's quads, then hidden-note ghosts): Shift adds,
 *    Ctrl/Cmd toggles; its line becomes active and the lane reveals it (`services.timeline.reveal`);
 *  - click a LINE to make it active (and select it; Shift adds, Ctrl/Cmd toggles); clicking again at the same spot
 *    cycles through the overlapping lines; invisible lines are pickable while "show invisible lines" is on;
 *  - click empty space to clear the selection;
 *  - "Set parent..." pick mode: the next click picks the new parent (empty space = no parent), then the parent
 *    prompt asks whether the line keeps its place or its numbers (Core.lines.setParent).
 * The provider sits under the gizmo and the sample handles and answers every point, so it owns plain clicks.
 */

import type { AppServices } from '../../../app/registry/types';
import { DIALOG } from '../../../app/dialogs/types';
import { Core } from '../../../core/commands/index';
import type { LineId, NoteId } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import type {
  OverlayDrawContext,
  OverlayHit,
  OverlayPointerEvent,
  PreviewOverlayProvider,
} from '../../../render/types';
import { filteredOut } from '../filters';
import { m } from '../preview.messages';
import { stageStateOf } from '../stageState';
import { ghostAt } from './ghosts';
import { dp, frameInfo, lineShape, linesAt, noteQuadPx, pathQuad, quadsOfNotes, strokeShape } from './shared';
import { overlayTheme } from './theme';

export type PickResult =
  | { readonly kind: 'note'; readonly note: NoteId; readonly ghost: boolean }
  | { readonly kind: 'line'; readonly lines: readonly LineId[] }
  | { readonly kind: 'empty' };

export type SelectMode = 'replace' | 'add' | 'toggle';

/** Same spot for click cycling (CSS px). */
const CYCLE_DISTANCE = 4;

export function modeOf(e: Pick<OverlayPointerEvent, 'shift' | 'ctrl'>): SelectMode {
  return e.ctrl ? 'toggle' : e.shift ? 'add' : 'replace';
}

/** What a click at (px, py) (overlay device px) would pick: a drawn note, a ghost, lines, or nothing. */
export function pickAt(services: AppServices, c: OverlayDrawContext, px: number, py: number): PickResult {
  const st = services.editor.getState();
  const [wx, wy] = c.pxToWorld(px, py);
  const note = c.engine.pickNote(c.list, wx, wy);
  if (note !== null) return { kind: 'note', note, ghost: false };
  if (st.preview.hiddenGhosts) {
    const g = ghostAt(c, px, py);
    if (g !== null) return { kind: 'note', note: g.note, ghost: true };
  }
  const store = services.store;
  const lines = linesAt(c, px, py, {
    doc: store?.doc ?? null,
    idx: store?.idx ?? null,
    invisible: st.preview.showInvisibleLines,
    skip: (line) => filteredOut(st.filters, line),
  });
  return lines.length > 0 ? { kind: 'line', lines } : { kind: 'empty' };
}

/** Selects a note (mode), makes its line active and reveals it in the lane. */
export function selectNote(services: AppServices, note: NoteId, mode: SelectMode): void {
  const store = services.store;
  const n = store?.doc.notes.get(note);
  if (n === undefined) return;
  const editor = services.editor.getState();
  editor.select(
    { notes: new Set([note]), primary: { kind: 'note', id: note }, anchor: { kind: 'note', id: note } },
    mode,
  );
  editor.setActiveLine(n.line);
  services.timeline?.reveal(Beats.toF64(n.t), 'center');
}

/** Makes a line active and selects it (mode). */
export function activateLine(services: AppServices, line: LineId, mode: SelectMode): void {
  const editor = services.editor.getState();
  editor.setActiveLine(line);
  editor.select({ lines: new Set([line]), primary: { kind: 'line', id: line } }, mode);
}

/**
 * The line a click picks among the candidates (topmost first): clicking again at the same spot with the same
 * candidates moves to the one after the active line (cycling through overlapping lines).
 */
export function cycleLine(
  candidates: readonly LineId[],
  active: LineId | null,
  last: { readonly x: number; readonly y: number; readonly candidates: readonly LineId[] } | null,
  x: number,
  y: number,
): LineId | null {
  const first = candidates[0];
  if (first === undefined) return null;
  const sameSpot =
    last !== null &&
    Math.hypot(last.x - x, last.y - y) <= CYCLE_DISTANCE &&
    last.candidates.length === candidates.length &&
    last.candidates.every((id, i) => candidates[i] === id);
  if (!sameSpot || active === null) return first;
  const i = candidates.indexOf(active);
  if (i < 0) return first;
  return candidates[(i + 1) % candidates.length] ?? first;
}

/** Runs "Set parent": the parent prompt (keep place / keep numbers), then Core.lines.setParent. */
export async function setParentFlow(services: AppServices, line: LineId, parent: LineId | null): Promise<void> {
  const store = services.store;
  if (store === null) return;
  const current = store.doc.lines.get(line)?.parent ?? null;
  if (current === parent) return;
  let mode: 'keepNumbers' | 'keepPlace' | null;
  if (services.registry.dialog(DIALOG.parentPrompt) !== undefined) {
    mode = await services.ui.openDialog(DIALOG.parentPrompt, { line, parent });
  } else {
    mode = await services.ui.choose<'keepPlace' | 'keepNumbers'>({
      title: { key: m.parentPromptTitle },
      options: [
        { value: 'keepPlace', label: { key: m.parentKeepPlace }, description: { key: m.parentKeepPlaceHelp } },
        { value: 'keepNumbers', label: { key: m.parentKeepNumbers }, description: { key: m.parentKeepNumbersHelp } },
      ],
    });
  }
  if (mode === null) return;
  const chosen = mode;
  const atSec = services.audio.transport.chartTime();
  const out = store.transact(
    { label: { key: parent === null ? m.txnClearParent : m.txnSetParent }, command: 'preview.setParent' },
    (tx) => {
      Core.lines.setParent(tx, { line, parent, mode: chosen, atSec });
    },
  );
  if (!out.ok) services.ui.toast(out.reason, { kind: 'error' });
}

/** Esc listeners of active pick modes (window, capture phase: they run whatever the focused element). */
const pickEsc = new WeakMap<object, () => void>();

/** Starts the "Set parent..." pick mode for a line (the next click in the preview picks the parent). */
export function startParentPick(services: AppServices, line: LineId): void {
  const state = stageStateOf(services);
  state.set({ parentPick: line });
  services.ui.setGestureHints([
    { chord: '', label: { key: m.parentPickHint } },
    { chord: 'Escape', label: { key: m.hintCancel } },
  ]);
  if (typeof window !== 'undefined' && !pickEsc.has(state)) {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      cancelParentPick(services);
    };
    window.addEventListener('keydown', onKey, true);
    pickEsc.set(state, () => {
      window.removeEventListener('keydown', onKey, true);
    });
  }
  services.scheduler.request('preview');
}

export function cancelParentPick(services: AppServices): void {
  const state = stageStateOf(services);
  const off = pickEsc.get(state);
  pickEsc.delete(state);
  off?.();
  if (state.getSnapshot().parentPick === null) return;
  state.set({ parentPick: null });
  services.ui.setGestureHints(null);
  services.scheduler.request('preview');
}

/**
 * A plain left click at an overlay point (also used by the gizmo when its line body is clicked without dragging).
 * `cssX` / `cssY` locate the click for cycling.
 */
export function clickAt(services: AppServices, c: OverlayDrawContext, e: OverlayPointerEvent): void {
  const state = stageStateOf(services);
  const pick = pickAt(services, c, e.px, e.py);
  const cssX = e.px / c.dpr;
  const cssY = e.py / c.dpr;
  const child = state.getSnapshot().parentPick;
  if (child !== null) {
    const parent = pick.kind === 'line' ? (pick.lines.find((id) => id !== child) ?? null) : null;
    if (pick.kind === 'note') return;
    cancelParentPick(services);
    void setParentFlow(services, child, parent);
    return;
  }
  const mode = modeOf(e);
  switch (pick.kind) {
    case 'note':
      state.lastClick = null;
      selectNote(services, pick.note, mode);
      return;
    case 'line': {
      const active = services.editor.getState().activeLine;
      const id = cycleLine(pick.lines, active, state.lastClick, cssX, cssY);
      state.lastClick = { x: cssX, y: cssY, candidates: pick.lines };
      if (id !== null) activateLine(services, id, mode);
      return;
    }
    case 'empty':
      state.lastClick = null;
      if (mode === 'replace') services.editor.getState().clearSelection();
      return;
  }
}

export function createSelectionOverlay(services: AppServices): PreviewOverlayProvider {
  return {
    id: 'preview.selection',
    order: 60,
    draw(c) {
      const st = services.editor.getState();
      const theme = overlayTheme();
      const ctx = c.ctx;
      const notes = st.selection.notes;
      if (notes.size > 0) {
        ctx.save();
        ctx.strokeStyle = theme.selection;
        ctx.lineWidth = dp(c, 2);
        for (const k of quadsOfNotes(c.list.notes, notes)) {
          pathQuad(ctx, noteQuadPx(c, c.list.notes, k));
          ctx.stroke();
        }
        ctx.restore();
      }
      if (st.selection.lines.size > 0) {
        const store = services.store;
        const { states } = frameInfo(c);
        for (const id of st.selection.lines) {
          const s = states.get(id);
          if (s === undefined || filteredOut(st.filters, id)) continue;
          const shape = lineShape(c, s, store?.doc ?? null, store?.idx ?? null);
          strokeShape(c, shape, theme.selection, 2, s.drawsNothing !== null);
        }
      }
      // Parent pick mode: highlight the line under consideration is done by the cursor; mark the child.
      const child = stageStateOf(services).getSnapshot().parentPick;
      if (child !== null) {
        const s = frameInfo(c).states.get(child);
        if (s !== undefined) {
          const store = services.store;
          strokeShape(c, lineShape(c, s, store?.doc ?? null, store?.idx ?? null), theme.warning, 2.5, true);
        }
      }
    },
    hitTest(c, px, py): OverlayHit {
      const pick = pickAt(services, c, px, py);
      const picking = stageStateOf(services).getSnapshot().parentPick !== null;
      return {
        provider: 'preview.selection',
        cursor: picking ? 'crosshair' : pick.kind === 'empty' ? 'default' : 'pointer',
        data: pick,
      };
    },
    onPointerDown(c, _hit, e) {
      if ((e.buttons & 1) === 0) return;
      clickAt(services, c, e);
    },
  };
}
